import React from 'react';
import PropTypes from 'prop-types';
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Components
import { PredicateElement } from './PredicateElement';
import { PredicateChildren } from './PredicateChildren';

// Context
import { RuleEditorContext } from '../context';

// Hooks
import { useCriteriaStoreUpdatePredicateOrder } from '../../../../../../state/criteria/hooks/useCriteriaStoreUpdatePredicateOrder';
import { useCriteriaStoreNonDeletedPredicateUuids } from '../../../../../../state/criteria/hooks/useCriteriaStoreNonDeletedPredicateUuids';

export const PredicateWrapper = ({ parentPredicateUuid, nestedLevel, predicateUuids = [] }) => {
    const { setEdited } = React.useContext(RuleEditorContext);
    const [activeUuid, setActiveUuid] = React.useState();
    const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));
    const updatePredicateOrder = useCriteriaStoreUpdatePredicateOrder();
    const nonDeletedPredicateUuids = useCriteriaStoreNonDeletedPredicateUuids(predicateUuids);
    const [collapsed, setCollapsed] = React.useState(
        predicateUuids.reduce(
            (acc, curr) => ({
                ...acc,
                [`predicate-collapse-${curr}`]: false
            }),
            {}
        )
    );

    function handleDragStart(event) {
        setActiveUuid(event.active.id);
    }

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active && over && active?.id !== over?.id) {
            const oldIndex = predicateUuids.indexOf(active.id);
            const newIndex = predicateUuids.indexOf(over.id);
            const sorted = arrayMove([...predicateUuids], oldIndex, newIndex);

            updatePredicateOrder(parentPredicateUuid, sorted);
            setEdited(true);
        }

        setActiveUuid(null);
    }

    function handleDragCancel() {
        setActiveUuid(null);
    }

    return predicateUuids?.length ? (
        <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            autoScroll={{ layoutShiftCompensation: false, enable: false }}
        >
            <SortableContext items={predicateUuids} strategy={verticalListSortingStrategy}>
                {predicateUuids.map((predicateUuid, index) => {
                    const collapseRef = `predicate-collapse-${predicateUuid}`;
                    return (
                        <React.Fragment key={index}>
                            <PredicateElement
                                {...{ predicateUuid, nestedLevel }}
                                canReorder={predicateUuids.length > 1}
                                collapsed={collapsed[collapseRef]}
                                onCollapse={() => {
                                    const newCollapsedState = !collapsed[collapseRef];

                                    setCollapsed({
                                        ...collapsed,
                                        [collapseRef]: newCollapsedState
                                    });
                                    // $(`#${collapseRef}`).collapse(newCollapsedState ? 'hide' : 'show');
                                }}
                                isLast={
                                    nonDeletedPredicateUuids.findIndex(predicateUuid) ===
                                    nonDeletedPredicateUuids.length - 1
                                }
                            >
                                <div className={`collapse ${collapsed[collapseRef] ? '' : 'in'}`} id={collapseRef}>
                                    <PredicateChildren uuid={predicateUuid} nestedLevel={nestedLevel + 1} />
                                </div>
                            </PredicateElement>
                        </React.Fragment>
                    );
                })}
            </SortableContext>
            <DragOverlay zIndex={5000}>
                {activeUuid && (
                    <div
                        style={{
                            cursor: 'grabbing',
                            padding: '5px 12px',
                            background: '#fff',
                            border: '2px solid #a2c3df'
                        }}
                    >
                        <PredicateElement
                            previewOnly={true}
                            predicateUuid={activeUuid}
                            canReorder={false}
                            collapsed={collapsed[`predicate-collapse-${activeUuid}`]}
                            onCollapse={() => {}}
                            isLast={
                                nonDeletedPredicateUuids.findIndex(activeUuid) === nonDeletedPredicateUuids.length - 1
                            }
                            nestedLevel={nestedLevel}
                        >
                            <div className={`collapse ${collapsed[`predicate-collapse-${activeUuid}`] ? '' : 'in'}`}>
                                <PredicateChildren uuid={activeUuid} nestedLevel={nestedLevel + 1} />
                            </div>
                        </PredicateElement>
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    ) : null;
};

export default PredicateWrapper;

PredicateWrapper.propTypes = {
    predicateUuids: PropTypes.arrayOf(PropTypes.string),
    parentPredicateUuid: PropTypes.string,
    nestedLevel: PropTypes.number
};
